import { addDays } from 'date-fns';
import {
  AlexaVisibilityResult,
  AlexaVisibilityRun
} from '@blisspointmedia/bpm-types/dist/AlexaVisibility';

// The results endpoint returns at most this many of the newest matches per request
// (`MAX_RESULTS` in bpm-lambdas' alexaVisibilityRunStore.ts).
export const RESULTS_REQUEST_CAP = 1_000;

// A window this short that still fills the cap can't be split further in a useful way.
const MIN_WINDOW_MS = 60 * 60 * 1000;

export type FetchResultsWindow = (
  dateFrom: string,
  dateTo: string
) => Promise<AlexaVisibilityResult[]>;

/**
 * Every result between `startMs` and `endMs`, however many there are. A single request is capped at
 * the newest 1,000, so whenever a window comes back full it is split in half and each half is
 * fetched on its own, down to one-hour windows. `complete` is false only if a one-hour window is
 * itself full, which would mean results were left out.
 */
export const fetchResultsInWindows = async (
  fetchWindow: FetchResultsWindow,
  startMs: number,
  endMs: number,
  cap = RESULTS_REQUEST_CAP
): Promise<{ results: AlexaVisibilityResult[]; complete: boolean }> => {
  const byId = new Map<number, AlexaVisibilityResult>();
  let complete = true;

  const load = async (from: number, to: number): Promise<void> => {
    const batch = await fetchWindow(new Date(from).toISOString(), new Date(to).toISOString());
    if (batch.length < cap) {
      batch.forEach(result => byId.set(result.id, result));
      return;
    }
    if (to - from <= MIN_WINDOW_MS) {
      complete = false;
      batch.forEach(result => byId.set(result.id, result));
      return;
    }
    const middle = Math.floor((from + to) / 2);
    await load(from, middle);
    // Both halves include `middle`: the server's bounds are inclusive and `created_at` has
    // sub-millisecond precision, so starting at `middle + 1` could skip rows. `byId` drops the overlap.
    await load(middle, to);
  };

  await load(startMs, endMs);
  return { results: Array.from(byId.values()), complete };
};

/** The window to fetch for a brand's whole history: a day before its first run to a day from now. */
export const getHistoryWindow = (
  runs: AlexaVisibilityRun[],
  now = new Date()
): { startMs: number; endMs: number } => {
  const starts = runs.map(run => new Date(run.startedAt).getTime()).filter(ms => !Number.isNaN(ms));
  const first =
    starts.length > 0 ? new Date(Math.min(...starts)) : new Date('2000-01-01T00:00:00Z');
  return { startMs: addDays(first, -1).getTime(), endMs: addDays(now, 1).getTime() };
};

// Written into the export so a reader (or Claude) knows what the fields mean without being told.
export const EXPORT_FIELD_GUIDE = {
  structure:
    'runs[] lists each scrape. results[] has one entry per query per run, oldest first; results[].runId matches runs[].id, and runs[].runNumber is the human run number.',
  'results[].query': 'The question Alexa for Shopping was asked.',
  'results[].createdAt': 'When the result was scraped (ISO timestamp).',
  'results[].response': "Alexa's full answer text.",
  'results[].followupQuestions': 'Follow-up questions Alexa suggested after answering.',
  'classification.targetBrandMentioned':
    'Whether the selected brand was mentioned. Visibility Score is the % of results where this is true.',
  'classification.targetBrandRank':
    'Where the selected brand ranked among the recommendations (1 is best); absent when it was not ranked.',
  'classification.brandsMentioned': 'Every brand named in the answer.',
  'classification.top3Recommendations':
    'The top three products recommended, in order. A product belongs to a brand when its name starts with the brand name (e.g. "Oura Ring 4" is Oura). The share of these slots a brand holds is its SOAR.',
  'classification.citationTypes':
    'How the selected brand was cited: Named Brand, Attribute/Feature, Competitive Comparison, or No Citation / Not Recommended. A result can have several.',
  'classification.citationStrength': 'strong, moderate, weak or none.',
  'classification.competitiveFraming':
    'When Alexa compared brands: favorable, neutral or unfavorable for the selected brand; not_applicable when it made no comparison.',
  'classification.keyAttributesCited': 'Product attributes cited for the selected brand.',
  'classification.competitorAttributes': 'Product attributes cited for each competitor brand.',
  'classification.citationSources': 'Publications and sites Alexa cited to support its answer.'
} as const;

export interface AllDataExport {
  about: {
    description: string;
    brand: string;
    company: string;
    exportedAt: string;
    runCount: number;
    resultCount: number;
    firstResultAt: string | null;
    lastResultAt: string | null;
    // False only if some results could not be fetched (see fetchResultsInWindows).
    complete: boolean;
    fieldGuide: typeof EXPORT_FIELD_GUIDE;
  };
  runs: AlexaVisibilityRun[];
  results: AlexaVisibilityResult[];
}

/** Every run and every result for a brand in one object, oldest first, ready to save as JSON. */
export const buildAllDataExport = ({
  company,
  brand,
  runs,
  results,
  complete,
  exportedAt = new Date()
}: {
  company: string;
  brand: string;
  runs: AlexaVisibilityRun[];
  results: AlexaVisibilityResult[];
  complete: boolean;
  exportedAt?: Date;
}): AllDataExport => {
  const sortedResults = [...results].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.id - b.id
  );
  const sortedRuns = [...runs].sort((a, b) => a.runNumber - b.runNumber);
  return {
    about: {
      description: `Everything Alexa Visibility has collected for ${brand}: every run and every query result, including full responses and classifications.`,
      brand,
      company,
      exportedAt: exportedAt.toISOString(),
      runCount: sortedRuns.length,
      resultCount: sortedResults.length,
      firstResultAt: sortedResults[0]?.createdAt ?? null,
      lastResultAt: sortedResults[sortedResults.length - 1]?.createdAt ?? null,
      complete,
      fieldGuide: EXPORT_FIELD_GUIDE
    },
    runs: sortedRuns,
    results: sortedResults
  };
};

/** A file name that is safe on every system, e.g. AlexaVisibility_AllData_WHOOP_2026-10-12.json. */
export const getAllDataFileName = (brand: string, exportedAt = new Date()): string =>
  `AlexaVisibility_AllData_${brand.replace(/[^A-Za-z0-9._-]+/g, '_')}_${exportedAt
    .toISOString()
    .slice(0, 10)}.json`;
